import { useRef, useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, RotateCcw } from 'lucide-react'
import { Button } from './Button'

export interface SignaturePadProps {
  value?: string
  onChange: (dataUrl: string | null) => void
  width?: number
  height?: number
  label?: string
  disabled?: boolean
  penColor?: string
  backgroundColor?: string
}

export function SignaturePad({
  value,
  onChange,
  width = 400,
  height = 200,
  label,
  disabled = false,
  penColor = '#000000',
  backgroundColor = '#ffffff',
}: SignaturePadProps) {
  const { t } = useTranslation('common')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [isEmpty, setIsEmpty] = useState(true)
  const lastPointRef = useRef<{ x: number; y: number } | null>(null)

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    canvas.width = width
    canvas.height = height

    // Fill background
    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, width, height)

    // Load existing signature if provided
    if (value) {
      const img = new Image()
      img.onload = () => {
        ctx.drawImage(img, 0, 0)
        setIsEmpty(false)
      }
      img.src = value
    }
  }, [width, height, backgroundColor, value])

  const getCoordinates = (
    e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>
  ): { x: number; y: number } => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    if ('touches' in e) {
      const touch = e.touches[0]
      return {
        x: (touch.clientX - rect.left) * scaleX,
        y: (touch.clientY - rect.top) * scaleY,
      }
    } else {
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      }
    }
  }

  const startDrawing = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (disabled) return
      e.preventDefault()
      setIsDrawing(true)
      lastPointRef.current = getCoordinates(e)
    },
    [disabled]
  )

  const draw = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
      if (!isDrawing || disabled) return
      e.preventDefault()

      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (!canvas || !ctx || !lastPointRef.current) return

      const currentPoint = getCoordinates(e)

      ctx.beginPath()
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y)
      ctx.lineTo(currentPoint.x, currentPoint.y)
      ctx.strokeStyle = penColor
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.stroke()

      lastPointRef.current = currentPoint
      setIsEmpty(false)
    },
    [isDrawing, disabled, penColor]
  )

  const stopDrawing = useCallback(() => {
    setIsDrawing(false)
    lastPointRef.current = null
  }, [])

  const clear = useCallback(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setIsEmpty(true)
    onChange(null)
  }, [backgroundColor, onChange])

  const save = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || isEmpty) return

    const dataUrl = canvas.toDataURL('image/png')
    onChange(dataUrl)
  }, [isEmpty, onChange])

  return (
    <div className="space-y-2">
      {label && <label className="label-text font-medium">{label}</label>}

      <div
        className={`border-2 rounded-lg overflow-hidden ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'border-base-300'
        }`}
        style={{ width: 'fit-content' }}
      >
        <canvas
          ref={canvasRef}
          className="touch-none"
          style={{
            width: `${width}px`,
            height: `${height}px`,
            cursor: disabled ? 'not-allowed' : 'crosshair',
          }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          leftIcon={<RotateCcw className="w-4 h-4" />}
          onClick={clear}
          disabled={disabled || isEmpty}
        >
          {t('signature_pad.clear')}
        </Button>
        <Button
          type="button"
          size="sm"
          leftIcon={<Check className="w-4 h-4" />}
          onClick={save}
          disabled={disabled || isEmpty}
        >
          {t('signature_pad.confirm_signature')}
        </Button>
      </div>

      {isEmpty && (
        <p className="text-sm text-base-content/60">
          {t('signature_pad.draw_hint')}
        </p>
      )}

      {value && !isEmpty && (
        <p className="text-sm text-success flex items-center gap-1">
          <Check className="w-4 h-4" />
          {t('signature_pad.captured')}
        </p>
      )}
    </div>
  )
}
